import {
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  NgZone,
  OnInit,
  ChangeDetectorRef,
  TemplateRef
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as fromLocatorUIStore from 'src/app/core/store';

@Component({
    selector: 'atlas-floating-overlay',
    templateUrl: './floating-overlay.component.html',
    styleUrls: ['./floating-overlay.component.less'],
    standalone: false
})
export class FloatingOverlayComponent implements OnInit, OnDestroy {
  @Input() title: string = '';
  @Input() panelWidth: number = 900;
  @Input() panelHeight: number = 500;
  @Input() headerIcon: string = '';

  @Output() close = new EventEmitter<void>();
  @Input() template!: TemplateRef<any>;

  @Input() panelLeft: number = 0;
  @Input() panelTop: number = 0;

  private isResizing: boolean = false;
  private resizeType: 'right' | 'bottom' | 'corner' = 'corner';
  private startX: number = 0;
  private startY: number = 0;
  private startWidth: number = 0;
  private startHeight: number = 0;
  private startLeft: number = 0;
  private startTop: number = 0;
  private startRight: number = 0;

  private isDragging: boolean = false;

  private readonly minWidth: number = 250;
  private readonly minHeight: number = 200;

  private destroy$ = new Subject<void>();

  @Input() storageKey: string = 'floatingOverlayState';

  constructor(
    private elementRef: ElementRef,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private locatorUIStore$: Store<fromLocatorUIStore.State>
  ) {}

  ngOnInit(): void {
    this.loadPanelState();

    fromEvent(window, 'resize')
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.onWindowResize());
  }
  private centerPanel(): void {
    this.panelLeft = (window.innerWidth - this.panelWidth) / 2;
    this.panelTop = (window.innerHeight - this.panelHeight) / 2;
  }

  private savePanelState(): void {
    const state = {
      width: this.panelWidth,
      height: this.panelHeight,
      left: this.panelLeft,
      top: this.panelTop
    };
    localStorage.setItem(this.storageKey, JSON.stringify(state));
  }

  private loadPanelState(): void {
    const savedState = localStorage.getItem(this.storageKey);
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        this.panelWidth = state.width ?? this.panelWidth;
        this.panelHeight = state.height ?? this.panelHeight;
        this.panelLeft =
          state.left ?? (window.innerWidth - this.panelWidth) / 2;
        this.panelTop =
          state.top ?? (window.innerHeight - this.panelHeight) / 2;
      } catch {
        console.warn('Failed to parse panel state from localStorage');
        this.centerPanel();
      }
    } else {
      this.centerPanel();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  startDrag(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startLeft = this.panelLeft;
    this.startTop = this.panelTop;

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onDragMove);
      document.addEventListener('mouseup', this.onDragUp);
    });

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'move';
  }

  startResize(event: MouseEvent, type: 'right' | 'bottom' | 'corner'): void {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.resizeType = type;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = this.panelWidth;
    this.startHeight = this.panelHeight;
    this.startLeft = this.panelLeft;
    this.startTop = this.panelTop;

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
    });

    document.body.style.userSelect = 'none';
    document.body.style.cursor = this.getCursor(type);
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (!this.isResizing) return;
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    if (this.resizeType === 'right' || this.resizeType === 'corner') {
      this.panelWidth = Math.max(this.minWidth, this.startWidth + deltaX);
    }
    if (this.resizeType === 'bottom' || this.resizeType === 'corner') {
      this.panelHeight = Math.max(this.minHeight, this.startHeight + deltaY);
    }

    this.cdr.detectChanges();
  };

  private onDragMove = (event: MouseEvent): void => {
    if (!this.isDragging) return;

    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;

    this.panelLeft = Math.max(
      0,
      Math.min(this.startLeft + deltaX, window.innerWidth - this.panelWidth)
    );
    this.panelTop = Math.max(
      0,
      Math.min(this.startTop + deltaY, window.innerHeight - this.panelHeight)
    );

    this.cdr.detectChanges();
  };

  private onMouseUp = (): void => {
    try {
      this.isResizing = false;
      this.savePanelState();
    } finally {
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
  };

  private onDragUp = (): void => {
    try {
      this.isDragging = false;
      this.savePanelState();
    } finally {
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
  };

  private getCursor(type: 'right' | 'bottom' | 'corner'): string {
    switch (type) {
      case 'right':
        return 'ew-resize';
      case 'bottom':
        return 'ns-resize';
      case 'corner':
        return 'nw-resize';
      default:
        return 'default';
    }
  }

  private onWindowResize(): void {
    const maxViewportWidth = window.innerWidth * 0.8;
    const maxViewportHeight = window.innerHeight * 0.8;

    this.panelWidth = Math.min(this.panelWidth, maxViewportWidth);
    this.panelHeight = Math.min(this.panelHeight, maxViewportHeight);

    this.panelLeft = Math.max(
      0,
      Math.min(this.panelLeft, window.innerWidth - this.panelWidth)
    );
    this.panelTop = Math.max(
      0,
      Math.min(this.panelTop, window.innerHeight - this.panelHeight)
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.locatorUIStore$.dispatch(fromLocatorUIStore.hideStatsOverlay());

    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.onDragUp);

    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }
}
